<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Direct Order Status"
                sample-csv-path="/updateDirectOrderStatusByImportSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Bulk Update Direct Order Status by Import </v-col>
                        <v-col cols="12" sm="12" md="6"> </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListDirectOrder"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td>{{ item.status }}</td>
                                <td>{{ item.error }}</td>
                                <td>{{ item.directOrderNumber }}</td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { DirectOrderLabelTextHelper } from '../../utils/labelTextHelper/DirectOrderLabelTextHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import CommonListReference from '../../components/CommonListReference.vue';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { DirectOrderHeaderHelper } from '../../utils/DirectOrderHeaderHelper';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';
import { UpdateDirectOrderStatusImportValidation } from '../../config/importValidationConfigs';

export default defineComponent({
    name: 'ImportUpdateDirectOrderStatus',

    components: {
        CommonListReference,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        const data = reactive({
            mappedCsv: null,
            previewHeaders: [DirectOrderHeaderHelper.getDirectOrderNumberHeaderItem()],
            fields: {
                directOrderNumber: { required: false, label: DirectOrderLabelTextHelper.getLabelText.directOrderNumber }
            },
            headers: [
                ...ImportHeaderHelper.getHeaders(),
                {
                    text: DirectOrderLabelTextHelper.getLabelText.directOrderNumber,
                    value: 'directOrderNumber'
                }
            ]
        });
        return {
            data
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListDirectOrder: PathDefinition.listDirectOrder,
            formLabelText: DirectOrderLabelTextHelper.getLabelText
        };
    },
    async created() {},
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            return ImportValidationHelper.validateRows(mappedCsv, UpdateDirectOrderStatusImportValidation);
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;
            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                for (const item in this.data.mappedCsv) {
                    await this.update(item);
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, UpdateDirectOrderStatusImportValidation);
        },
        async update(idx) {
            const csvData = this.data.mappedCsv[idx];
            const response = await this.updateStatus(csvData);
            this.processResponse(response, idx, csvData);
        },
        async updateStatus(csvData) {
            const request = _.pick(csvData, ['directOrderNumber']);
            const requestUrl = `/api/customer/directOrder/updateStatusReadyToShip`;
            return await fetchWrapper.post(requestUrl, request);
        },
        processResponse(response, idx, csvData) {
            if (response.success) {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
            } else {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = response.errors;
            }
            this.data.mappedCsv.splice(idx, 1, csvData);
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
